import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import {
  Box,
  Button,
  Checkbox,
  Chip,
  IconButton,
  InputAdornment,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import DescriptionOutlined from "@mui/icons-material/DescriptionOutlined";
import RefreshRoundedIcon from "@mui/icons-material/RefreshRounded";
import ViewColumnOutlined from "@mui/icons-material/ViewColumnOutlined";
import FileUploadOutlined from "@mui/icons-material/FileUploadOutlined";
import AddRoundedIcon from "@mui/icons-material/AddRounded";
import SearchIcon from "@mui/icons-material/Search";
import moment from "moment";
import useStyles from "./style";
import { mapContractRecord } from "./mapContract";
import ContractsApi from "Services/Contracts";
import { hideLoader, showLoader } from "Redux/Loader/actions";
import { showSnackbar } from "Redux/SnackBar/actions";

const PAGE_SIZE = 5;
const EXPIRING_DAYS_OPTIONS = [7, 15, 30, 60, 90];

const formatEndDate = (value) => (value ? moment(value).format("DD/MM/YYYY") : "--");
const formatValue = (value, currency) =>
  typeof value === "number" ? `${value.toLocaleString("en-US")} ${currency || ""}`.trim() : "--";
const isOverdue = (value) => Boolean(value) && moment(value).isBefore(moment(), "day");

const Contracts = () => {
  const classes = useStyles();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const [rows, setRows] = useState([]);
  const [totalRecords, setTotalRecords] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [searchText, setSearchText] = useState("");
  const [typeFilter, setTypeFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [expiringFilter, setExpiringFilter] = useState("");
  const [selectedIds, setSelectedIds] = useState([]);

  const fetchContracts = useCallback(async () => {
    dispatch(showLoader("Loading please wait..."));
    setLoading(true);

    try {
      const params = { page, limit: PAGE_SIZE };
      const trimmedSearch = searchText.trim();
      if (trimmedSearch) {
        params.search = trimmedSearch;
      }

      const response = await ContractsApi.getContractsList(params);
      const payload = response?.data?.data ?? {};
      const contracts = Array.isArray(payload?.data) ? payload.data : [];

      setRows(contracts.map(mapContractRecord));
      setTotalRecords(payload?.totalRecords ?? contracts.length);
      setTotalPages(payload?.totalPages ?? 1);
    } catch (error) {
      dispatch(
        showSnackbar({
          message: error?.response?.data?.message || "Failed to fetch contracts",
          autoHideDuration: 3000,
          anchorOrigin: {
            vertical: "top",
            horizontal: "right",
          },
          variant: "error",
        })
      );
      setRows([]);
      setTotalRecords(0);
      setTotalPages(1);
    } finally {
      dispatch(hideLoader());
      setLoading(false);
    }
  }, [dispatch, page, searchText]);

  useEffect(() => {
    const timer = window.setTimeout(() => {
      fetchContracts();
    }, 350);

    return () => window.clearTimeout(timer);
  }, [fetchContracts]);

  const typeOptions = useMemo(
    () => Array.from(new Set(rows.map((contract) => contract.type).filter(Boolean))),
    [rows]
  );
  const statusOptions = useMemo(
    () => Array.from(new Set(rows.map((contract) => contract.status).filter(Boolean))),
    [rows]
  );

  const filteredRows = useMemo(() => {
    return rows.filter((contract) => {
      const matchesType = !typeFilter || contract.type === typeFilter;
      const matchesStatus = !statusFilter || contract.status === statusFilter;
      const matchesExpiring =
        !expiringFilter ||
        (contract.endDate &&
          moment(contract.endDate).diff(moment(), "days") <= Number(expiringFilter) &&
          moment(contract.endDate).diff(moment(), "days") >= 0);

      return matchesType && matchesStatus && matchesExpiring;
    });
  }, [rows, typeFilter, statusFilter, expiringFilter]);

  const stats = useMemo(
    () => ({
      total: totalRecords,
      draft: filteredRows.filter((contract) => contract.status === "draft").length,
      approved: filteredRows.filter((contract) => contract.status === "approved").length,
      expiringSoon: filteredRows.filter((contract) => {
        if (!contract.endDate) {
          return false;
        }
        const daysUntilEnd = moment(contract.endDate).diff(moment(), "days");
        return daysUntilEnd >= 0 && daysUntilEnd <= 30;
      }).length,
    }),
    [totalRecords, filteredRows]
  );

  const handleReset = () => {
    setSearchText("");
    setTypeFilter("");
    setStatusFilter("");
    setExpiringFilter("");
    setPage(1);
  };

  const handleToggleSelectAll = (event) => {
    setSelectedIds(event.target.checked ? filteredRows.map((contract) => contract.id) : []);
  };

  const handleToggleSelectRow = (event, id) => {
    event.stopPropagation();
    setSelectedIds((previous) =>
      previous.includes(id) ? previous.filter((rowId) => rowId !== id) : [...previous, id]
    );
  };

  const handleOpenContract = (id) => {
    navigate(`/contracts/${id}`);
  };

  const allFilteredSelected =
    filteredRows.length > 0 && filteredRows.every((contract) => selectedIds.includes(contract.id));

  return (
    <Box className={classes.page}>
      <Box className={classes.headerBar}>
        <Box className={classes.headerLeft}>
          <Box className={classes.headerIconBadge}>
            <DescriptionOutlined />
          </Box>
          <Typography className={classes.headerTitle}>Contracts</Typography>
          <Typography className={classes.headerCount}>{totalRecords} total</Typography>
        </Box>

        <Box className={classes.headerActions}>
          <IconButton aria-label="refresh" onClick={fetchContracts}>
            <RefreshRoundedIcon fontSize="small" />
          </IconButton>
          <Button variant="outlined" startIcon={<ViewColumnOutlined />}>
            Views
          </Button>
          <Button variant="outlined" startIcon={<FileUploadOutlined />}>
            Import
          </Button>
          <Button variant="contained" color="primary" startIcon={<AddRoundedIcon />}>
            New contract
          </Button>
        </Box>
      </Box>

      <Box className={classes.statsBar}>
        <Box className={classes.statCard}>
          <Typography className={classes.statLabel}>Total contracts</Typography>
          <Typography className={classes.statValue}>{stats.total}</Typography>
          <Typography className={classes.statSubtext}>{totalPages} pages</Typography>
        </Box>
        <Box className={classes.statCard}>
          <Typography className={classes.statLabel}>Draft</Typography>
          <Typography className={classes.statValue}>{stats.draft}</Typography>
          <Typography className={classes.statSubtext}>On current page</Typography>
        </Box>
        <Box className={classes.statCard}>
          <Typography className={classes.statLabel}>Approved</Typography>
          <Typography className={classes.statValue}>{stats.approved}</Typography>
          <Typography className={classes.statSubtext}>On current page</Typography>
        </Box>
        <Box className={classes.statCard}>
          <Typography className={classes.statLabel}>Expiring (30d)</Typography>
          <Typography className={classes.statValue}>{stats.expiringSoon}</Typography>
          <Typography className={classes.statSubtext}>Based on end date</Typography>
        </Box>
      </Box>

      <Box className={classes.filterBar}>
        <TextField
          className={classes.searchField}
          placeholder="Search by title, reference..."
          size="small"
          value={searchText}
          onChange={(event) => {
            setSearchText(event.target.value);
            setPage(1);
          }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" sx={{ color: "#98A2B3" }} />
              </InputAdornment>
            ),
          }}
        />

        <TextField
          select
          className={classes.filterSelect}
          size="small"
          value={typeFilter}
          onChange={(event) => setTypeFilter(event.target.value)}
        >
          <MenuItem value="">--</MenuItem>
          {typeOptions.map((type) => (
            <MenuItem key={type} value={type}>
              {type}
            </MenuItem>
          ))}
        </TextField>

        <TextField
          select
          className={classes.filterSelect}
          size="small"
          value={statusFilter}
          onChange={(event) => setStatusFilter(event.target.value)}
        >
          <MenuItem value="">--</MenuItem>
          {statusOptions.map((status) => (
            <MenuItem key={status} value={status}>
              {status}
            </MenuItem>
          ))}
        </TextField>

        <TextField
          select
          className={classes.filterSelect}
          size="small"
          value={expiringFilter}
          onChange={(event) => setExpiringFilter(event.target.value)}
        >
          <MenuItem value="">Expiring days --</MenuItem>
          {EXPIRING_DAYS_OPTIONS.map((days) => (
            <MenuItem key={days} value={days}>
              {days} days
            </MenuItem>
          ))}
        </TextField>

        <Button variant="outlined" onClick={handleReset}>
          Reset
        </Button>
      </Box>

      <TableContainer className={classes.tableContainer}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell padding="checkbox">
                <Checkbox
                  size="small"
                  checked={allFilteredSelected}
                  indeterminate={
                    !allFilteredSelected &&
                    filteredRows.some((contract) => selectedIds.includes(contract.id))
                  }
                  onChange={handleToggleSelectAll}
                />
              </TableCell>
              <TableCell className={classes.tableHeadCell}>Contract name</TableCell>
              <TableCell className={classes.tableHeadCell}>Reference</TableCell>
              <TableCell className={classes.tableHeadCell}>Type</TableCell>
              <TableCell className={classes.tableHeadCell}>Status</TableCell>
              <TableCell className={classes.tableHeadCell}>Counterparty</TableCell>
              <TableCell className={classes.tableHeadCell}>Owner</TableCell>
              <TableCell className={classes.tableHeadCell}>End date</TableCell>
              <TableCell className={classes.tableHeadCell}>Value</TableCell>
              <TableCell className={classes.tableHeadCell}>Template</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {!loading && filteredRows.length === 0 ? (
              <TableRow>
                <TableCell colSpan={10} className={classes.emptyState}>
                  No contracts found for the current filters.
                </TableCell>
              </TableRow>
            ) : (
              filteredRows.map((contract) => (
                <TableRow
                  key={contract.id}
                  hover
                  className={classes.bodyRow}
                  onClick={() => handleOpenContract(contract.id)}
                >
                  <TableCell padding="checkbox">
                    <Checkbox
                      size="small"
                      checked={selectedIds.includes(contract.id)}
                      onClick={(event) => event.stopPropagation()}
                      onChange={(event) => handleToggleSelectRow(event, contract.id)}
                    />
                  </TableCell>
                  <TableCell className={classes.contractNameCell}>
                    <Typography className={classes.contractNameLink} noWrap>
                      {contract.name}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography className={classes.referenceText}>{contract.reference}</Typography>
                  </TableCell>
                  <TableCell>
                    <Chip label={contract.type} size="small" className={classes.typeChip} />
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={contract.status}
                      size="small"
                      className={`${classes.statusChip} ${
                        contract.status === "approved" ? classes.statusApproved : classes.statusDraft
                      }`}
                    />
                  </TableCell>
                  <TableCell>{contract.counterparty || "--"}</TableCell>
                  <TableCell>{contract.owner || "--"}</TableCell>
                  <TableCell>
                    <Typography className={isOverdue(contract.endDate) ? classes.endDateOverdue : undefined}>
                      {formatEndDate(contract.endDate)}
                    </Typography>
                  </TableCell>
                  <TableCell className={!contract.value ? classes.cellMuted : undefined}>
                    {formatValue(contract.value, contract.valueCurrency)}
                  </TableCell>
                  <TableCell className={!contract.template ? classes.cellMuted : undefined}>
                    {contract.template || "--"}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Box className={classes.footerBar}>
        <Typography className={classes.footerText}>
          {totalRecords === 0
            ? "Showing 0 contracts"
            : `Showing ${(page - 1) * PAGE_SIZE + 1} to ${Math.min(
                page * PAGE_SIZE,
                totalRecords
              )} of ${totalRecords} contracts`}
        </Typography>

        <Box className={classes.paginationGroup}>
          <Button
            variant="outlined"
            disabled={page <= 1}
            onClick={() => setPage((previous) => Math.max(1, previous - 1))}
          >
            Prev
          </Button>
          <Box className={classes.paginationPage}>{page}</Box>
          <Button
            variant="outlined"
            disabled={page >= totalPages}
            onClick={() => setPage((previous) => Math.min(totalPages, previous + 1))}
          >
            Next
          </Button>
        </Box>
      </Box>
    </Box>
  );
};

export default Contracts;
